import React from 'react';
import { Competence, Service } from '../../../../../../shared/types/config';

interface CompetencesTabProps {
  competences: Competence[];
  searchCompetence: string;
  onSearchChange: (value: string) => void;
  filterType: 'all' | 'normal' | 'specialite';
  onFilterTypeChange: (value: 'all' | 'normal' | 'specialite') => void;
  filterService: number | 'all';
  onFilterServiceChange: (value: number | 'all') => void;
  onAdd: () => void;
  onEdit: (competence: Competence) => void;
  onDelete: (id: number) => void;
  services: Service[];
}

const CompetencesTab: React.FC<CompetencesTabProps> = ({
  competences,
  searchCompetence,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  filterService,
  onFilterServiceChange,
  onAdd,
  onEdit,
  onDelete,
  services,
}) => {
  const filteredCompetences = competences.filter(c => {
    const matchesSearch = c.nom.toLowerCase().includes(searchCompetence.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(searchCompetence.toLowerCase());
    const matchesType = filterType === 'all' ||
      (filterType === 'normal' && !c.is_specialite) ||
      (filterType === 'specialite' && c.is_specialite);
    const matchesService = filterService === 'all' || c.id_service === filterService;
    return matchesSearch && matchesType && matchesService;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Rechercher une compétence..."
              value={searchCompetence}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
            />
            <svg className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-cockpit-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={onAdd}
            className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2 shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-cockpit-text-2">Type :</label>
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value as 'all' | 'normal' | 'specialite')}
              className="rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-1.5 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60"
            >
              <option value="all">Tous</option>
              <option value="normal">Compétences normales</option>
              <option value="specialite">Spécialités</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-cockpit-text-2">Service :</label>
            <select
              value={filterService}
              onChange={(e) => onFilterServiceChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-1.5 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60"
            >
              <option value="all">Tous</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>{service.nom}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[#1a2537]">
            <tr>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Ordre</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Type</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Service</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Description</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompetences.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-cockpit-text-2">
                  Aucune compétence trouvée
                </td>
              </tr>
            ) : (
              filteredCompetences.map((competence) => {
                const service = services.find(s => s.id === competence.id_service);
                return (
                  <tr key={competence.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 text-cockpit-text-2">{competence.id}</td>
                    <td className="px-4 py-3 text-cockpit-text-2">
                      {competence.ordre !== undefined && competence.ordre !== null ? (
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded text-xs border border-emerald-500/40 font-mono">
                          {competence.ordre}
                        </span>
                      ) : (
                        <span className="text-cockpit-muted text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-cockpit-text font-medium">{competence.nom}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        competence.is_specialite
                          ? 'bg-cockpit-violet/20 text-cockpit-violet border border-cockpit-violet/40'
                          : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                      }`}>
                        {competence.is_specialite ? 'Spécialité' : 'Compétence'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-cockpit-text-2">
                      {service ? service.nom : competence.id_service ? `Service #${competence.id_service}` : 'Générale'}
                    </td>
                    <td className="px-4 py-3 text-cockpit-text-2 max-w-xs truncate">
                      {competence.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(competence)}
                          className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 p-1.5 text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => competence.id && onDelete(competence.id)}
                          className="rounded border border-red-500/35 bg-red-500/10 p-1.5 text-red-300 hover:bg-red-500/20 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompetencesTab;
